// 스케줄 관리 페이지 (학원 스케줄 포함)

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Switch,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Stack,
  Checkbox,
  FormControlLabel,
  FormGroup,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SchoolIcon from '@mui/icons-material/School';
import { useSupabaseParentAuth } from '../hooks/useSupabaseParentAuth';
import { getAccessToken } from '../../../lib/authUtils';

// Python 형식: 0=월요일, 6=일요일 (DB와 동일)
const DAYS_OF_WEEK = ['월', '화', '수', '목', '금', '토', '일'];
// 학원 스케줄용: 1=월요일, 7=일요일
const DAYS_MAP: { [key: number]: string } = {
  1: '월',
  2: '화',
  3: '수',
  4: '목',
  5: '금',
  6: '토',
  7: '일',
};

// 자녀 타입
interface Child {
  id: string;
  name: string;
  organizationName: string;
  scheduleType?: string; // 'weekly' | 'period'
}

// 스케줄 타입
interface Schedule {
  id?: string;
  studentId: string;
  dayOfWeek: number;
  expectedCheckIn: string;
  expectedCheckOut: string;
  isActive: boolean;
}

// 학원 스케줄 타입
interface AcademySchedule {
  id: string;
  student_id: string;
  parent_id: string;
  organization_id: string;
  academy_name: string;
  days_of_week: number[];
  start_time: string;
  end_time: string;
  travel_minutes: number;
  grace_minutes: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// 학원 스케줄 폼 타입
interface AcademyScheduleForm {
  academy_name: string;
  days_of_week: number[];
  start_time: string;
  end_time: string;
  travel_minutes: number;
  grace_minutes: number;
}

const defaultAcademyForm: AcademyScheduleForm = {
  academy_name: '',
  days_of_week: [],
  start_time: '15:00',
  end_time: '17:00',
  travel_minutes: 15,
  grace_minutes: 10,
};

export default function SchedulePage() {
  const { childId } = useParams<{ childId: string }>();
  const navigate = useNavigate();
  const { parent } = useSupabaseParentAuth();

  const [children, setChildren] = useState<Child[]>([]);
  const [selectedChildId, setSelectedChildId] = useState<string>(childId || '');
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [academySchedules, setAcademySchedules] = useState<AcademySchedule[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // 학원 스케줄 모달 상태
  const [academyDialogOpen, setAcademyDialogOpen] = useState(false);
  const [academyForm, setAcademyForm] = useState<AcademyScheduleForm>(defaultAcademyForm);
  const [editingAcademyId, setEditingAcademyId] = useState<string | null>(null);
  const [academySaving, setAcademySaving] = useState(false);

  useEffect(() => {
    if (parent?.id) {
      loadChildren();
    }
  }, [parent?.id]);

  useEffect(() => {
    if (selectedChildId) {
      // 교시 스케줄 조직이면 교시 스케줄 페이지로 이동
      const child = children.find(c => c.id === selectedChildId);
      if (child?.scheduleType === 'period') {
        navigate(`/parent/period-schedule/${selectedChildId}`, { replace: true });
        return;
      }
      loadSchedules();
      loadAcademySchedules();
    }
  }, [selectedChildId, children]);

  const loadChildren = async () => {
    try {
      // 백엔드 API를 통해 자녀 목록 조회 (RLS 우회)
      const accessToken = await getAccessToken();
      

      if (!accessToken) {
        setError('로그인이 필요합니다.');
        return;
      }

      const response = await fetch('/api/v1/parent/children', {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || '자녀 목록 조회 실패');
      }

      const data = await response.json();

      const childrenData: Child[] = data.map((child: any) => ({
        id: child.id,
        name: child.name || '이름 없음',
        organizationName: child.organization_name || '독서실',
        scheduleType: child.schedule_type || 'weekly',
      }));

      setChildren(childrenData);

      if (childrenData.length > 0 && !selectedChildId) {
        setSelectedChildId(childrenData[0].id);
      }
    } catch (err: any) {
      console.error('자녀 조회 에러:', err);
      setError(err.message || '자녀 목록을 불러오는데 실패했습니다.');
    }
  };

  const loadSchedules = async () => {
    if (!selectedChildId) return;

    setIsLoading(true);
    setError('');

    try {
      // 백엔드 API를 통해 스케줄 조회 (RLS 우회)
      const accessToken = await getAccessToken();
      

      if (!accessToken) {
        throw new Error('로그인이 필요합니다.');
      }

      const response = await fetch(`/api/v1/parent/children/${selectedChildId}/schedules`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      let dbSchedules: any[] = [];
      if (response.ok) {
        dbSchedules = await response.json();
      } else {
        console.error('스케줄 조회 실패:', await response.text());
        // 에러여도 기본값 사용
      }

      // 요일별 스케줄 초기화
      const scheduleMap: { [key: number]: Schedule } = {};

      // 백엔드 API는 camelCase로 반환 (expectedCheckIn, expectedCheckOut)
      (dbSchedules || []).forEach((schedule: any) => {
        // camelCase (API 응답) 또는 snake_case (이전 형식) 모두 지원
        const dayOfWeek = schedule.dayOfWeek ?? schedule.day_of_week;
        const checkIn = schedule.expectedCheckIn || schedule.expected_check_in || '09:00';
        const checkOut = schedule.expectedCheckOut || schedule.expected_check_out || '22:00';
        const isActive = schedule.isActive ?? schedule.is_active ?? true;

        scheduleMap[dayOfWeek] = {
          id: schedule.id,
          studentId: schedule.studentId || schedule.student_id,
          dayOfWeek: dayOfWeek,
          expectedCheckIn: checkIn,
          expectedCheckOut: checkOut,
          isActive: isActive,
        };
      });

      // 0-6 (일-토) 스케줄 생성
      const allSchedules: Schedule[] = [];
      for (let i = 0; i < 7; i++) {
        allSchedules.push(
          scheduleMap[i] || {
            studentId: selectedChildId,
            dayOfWeek: i,
            expectedCheckIn: '09:00',
            expectedCheckOut: '22:00',
            isActive: i >= 1 && i <= 5, // 월-금만 기본 활성화
          }
        );
      }

      setSchedules(allSchedules);
    } catch (err: any) {
      console.error('스케줄 조회 오류:', err);
      // 에러가 나도 기본값 사용
      const defaultSchedules: Schedule[] = [];
      for (let i = 0; i < 7; i++) {
        defaultSchedules.push({
          studentId: selectedChildId,
          dayOfWeek: i,
          expectedCheckIn: '09:00',
          expectedCheckOut: '22:00',
          isActive: i >= 1 && i <= 5,
        });
      }
      setSchedules(defaultSchedules);
    } finally {
      setIsLoading(false);
    }
  };

  const loadAcademySchedules = async () => {
    if (!selectedChildId) return;

    try {
      // 백엔드 API를 통해 학원 스케줄 조회 (RLS 우회)
      const accessToken = await getAccessToken();
      

      if (!accessToken) {
        console.error('로그인이 필요합니다.');
        return;
      }

      const response = await fetch(`/api/v1/parent/children/${selectedChildId}/academy-schedules`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        console.error('학원 스케줄 조회 실패:', await response.text());
        return;
      }

      const data = await response.json();
      setAcademySchedules(data || []);
    } catch (err: any) {
      console.error('학원 스케줄 조회 오류:', err);
    }
  };

  const handleChildChange = (event: any) => {
    const newChildId = event.target.value as string;
    setSelectedChildId(newChildId);
    navigate(`/parent/schedule/${newChildId}`);
  };

  const handleScheduleChange = (dayOfWeek: number, field: keyof Schedule, value: any) => {
    setSchedules((prev) =>
      prev.map((schedule) =>
        schedule.dayOfWeek === dayOfWeek ? { ...schedule, [field]: value } : schedule
      )
    );
  };

  const handleSaveSchedules = async () => {
    if (!selectedChildId) return;

    setIsSaving(true);
    setError('');
    setSuccess('');

    try {
      // 백엔드 API를 통해 스케줄 일괄 저장 (RLS 우회)
      const accessToken = await getAccessToken();
      

      if (!accessToken) {
        throw new Error('로그인이 필요합니다.');
      }

      const schedulesData = schedules.map(schedule => ({
        day_of_week: schedule.dayOfWeek,
        expected_arrival_time: `${schedule.expectedCheckIn}:00`,
        expected_departure_time: `${schedule.expectedCheckOut}:00`,
        arrival_grace_minutes: 30,
        departure_grace_minutes: 30,
        is_active: schedule.isActive,
      }));

      const response = await fetch(`/api/v1/parent/children/${selectedChildId}/schedule/weekly`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ schedules: schedulesData }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || '스케줄 저장 실패');
      }

      // 저장 성공 응답 데이터 확인
      const savedData = await response.json();

      setSuccess('스케줄이 저장되었습니다.');
      setTimeout(() => setSuccess(''), 3000);

      // 저장 후 다시 로드하여 ID 업데이트
      await loadSchedules();
    } catch (err: any) {
      console.error('스케줄 저장 오류:', err);
      setError(err.message || '스케줄 저장에 실패했습니다.');
    } finally {
      setIsSaving(false);
    }
  };

  // ========== 학원 스케줄 관리 ==========
  const openAcademyDialog = (schedule?: AcademySchedule) => {
    if (schedule) {
      setEditingAcademyId(schedule.id);
      setAcademyForm({
        academy_name: schedule.academy_name,
        days_of_week: schedule.days_of_week,
        start_time: schedule.start_time.slice(0, 5), // "17:00:00" -> "17:00"
        end_time: schedule.end_time.slice(0, 5),
        travel_minutes: schedule.travel_minutes || 15,
        grace_minutes: schedule.grace_minutes,
      });
    } else {
      setEditingAcademyId(null);
      setAcademyForm(defaultAcademyForm);
    }
    setAcademyDialogOpen(true);
  };

  const closeAcademyDialog = () => {
    setAcademyDialogOpen(false);
    setEditingAcademyId(null);
    setAcademyForm(defaultAcademyForm);
  };

  const handleAcademyFormChange = (field: keyof AcademyScheduleForm, value: any) => {
    setAcademyForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleDayToggle = (day: number) => {
    setAcademyForm((prev) => {
      const days = prev.days_of_week.includes(day)
        ? prev.days_of_week.filter((d) => d !== day)
        : [...prev.days_of_week, day].sort((a, b) => a - b);
      return { ...prev, days_of_week: days };
    });
  };

  const saveAcademySchedule = async () => {
    if (!selectedChildId) return;

    // 유효성 검사
    if (!academyForm.academy_name.trim()) {
      setError('학원명을 입력해주세요.');
      return;
    }
    if (academyForm.days_of_week.length === 0) {
      setError('최소 하나의 요일을 선택해주세요.');
      return;
    }
    if (academyForm.start_time >= academyForm.end_time) {
      setError('복귀 시간은 외출 시간보다 늦어야 합니다.');
      return;
    }

    setAcademySaving(true);
    setError('');

    try {
      // 백엔드 API를 통해 학원 스케줄 저장 (RLS 우회)
      const accessToken = await getAccessToken();
      

      if (!accessToken) {
        throw new Error('로그인이 필요합니다.');
      }

      const scheduleData = {
        student_id: selectedChildId,
        academy_name: academyForm.academy_name.trim(),
        days_of_week: academyForm.days_of_week,
        start_time: academyForm.start_time,
        end_time: academyForm.end_time,
        travel_minutes: academyForm.travel_minutes,
        grace_minutes: academyForm.grace_minutes,
      };

      if (editingAcademyId) {
        // 수정
        const response = await fetch(`/api/v1/parent/academy-schedules/${editingAcademyId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(scheduleData),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || '학원 스케줄 수정 실패');
        }
        setSuccess('학원 스케줄이 수정되었습니다.');
      } else {
        // 생성
        const response = await fetch(`/api/v1/parent/children/${selectedChildId}/academy-schedules`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(scheduleData),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || '학원 스케줄 추가 실패');
        }
        setSuccess('학원 스케줄이 추가되었습니다.');
      }

      setTimeout(() => setSuccess(''), 3000);
      closeAcademyDialog();
      loadAcademySchedules();
    } catch (err: any) {
      console.error('학원 스케줄 저장 오류:', err);
      setError(err.message || '학원 스케줄 저장에 실패했습니다.');
    } finally {
      setAcademySaving(false);
    }
  };

  const deleteAcademySchedule = async (scheduleId: string) => {
    if (!window.confirm('이 학원 스케줄을 삭제하시겠습니까?')) return;

    try {
      // 백엔드 API를 통해 학원 스케줄 삭제 (RLS 우회)
      const accessToken = await getAccessToken();
      

      if (!accessToken) {
        throw new Error('로그인이 필요합니다.');
      }

      const response = await fetch(`/api/v1/parent/academy-schedules/${scheduleId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok && response.status !== 204) {
        const errorData = await response.json();
        throw new Error(errorData.detail || '학원 스케줄 삭제 실패');
      }

      setSuccess('학원 스케줄이 삭제되었습니다.');
      setTimeout(() => setSuccess(''), 3000);
      loadAcademySchedules();
    } catch (err: any) {
      console.error('학원 스케줄 삭제 오류:', err);
      setError(err.message || '학원 스케줄 삭제에 실패했습니다.');
    }
  };

  const formatDays = (days: number[]) => {
    return days.map((d) => DAYS_MAP[d] || '').join('·');
  };

  if (children.length === 0 && !isLoading) {
    return (
      <Box>
        <Alert severity="info">
          등록된 자녀가 없습니다. 독서실에 문의하여 자녀를 연결해주세요.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        스케줄 관리
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        자녀의 주간 스케줄과 학원 외출 시간을 설정할 수 있습니다.
      </Typography>

      {/* 자녀 선택 */}
      <Box sx={{ mb: 3 }}>
        <FormControl fullWidth sx={{ maxWidth: 300 }}>
          <InputLabel>자녀 선택</InputLabel>
          <Select value={selectedChildId} label="자녀 선택" onChange={handleChildChange}>
            {children.map((child) => (
              <MenuItem key={child.id} value={child.id}>
                {child.name} - {child.organizationName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* ========== 학원 스케줄 섹션 ========== */}
          <Paper sx={{ p: 3, mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SchoolIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  학원 스케줄
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => openAcademyDialog()}
                size="small"
              >
                학원 추가
              </Button>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              학원 외출/복귀 시간을 설정하면, 예정 시간에 외출하지 않거나 복귀하지 않을 경우 알림을 받을 수 있습니다.
            </Typography>

            {academySchedules.length === 0 ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                등록된 학원 스케줄이 없습니다. "학원 추가" 버튼을 눌러 학원 스케줄을 등록해주세요.
              </Alert>
            ) : (
              <Stack spacing={2}>
                {academySchedules.map((schedule) => (
                  <Paper
                    key={schedule.id}
                    variant="outlined"
                    sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {schedule.academy_name}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                        <Chip
                          label={formatDays(schedule.days_of_week)}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                        <Chip
                          label={`${schedule.start_time.slice(0, 5)} ~ ${schedule.end_time.slice(0, 5)}`}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={`이동 ${schedule.travel_minutes || 15}분`}
                          size="small"
                          variant="outlined"
                          sx={{ color: 'text.secondary' }}
                        />
                        <Chip
                          label={`여유 ${schedule.grace_minutes}분`}
                          size="small"
                          variant="outlined"
                          sx={{ color: 'text.secondary' }}
                        />
                      </Box>
                    </Box>
                    <Box>
                      <IconButton size="small" onClick={() => openAcademyDialog(schedule)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => deleteAcademySchedule(schedule.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Paper>
                ))}
              </Stack>
            )}
          </Paper>

          <Divider sx={{ my: 4 }} />

          {/* ========== 주간 스케줄 섹션 ========== */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              저장된 주간 스케줄
            </Typography>

            {/* 저장된 스케줄 카드 표시 */}
            {schedules.filter(s => s.isActive && s.id).length === 0 ? (
              <Alert severity="info">
                저장된 주간 스케줄이 없습니다. 아래에서 스케줄을 설정하고 저장해주세요.
              </Alert>
            ) : (
              <Stack spacing={1}>
                {schedules
                  .filter(s => s.isActive && s.id)
                  .map((schedule) => (
                    <Paper
                      key={schedule.dayOfWeek}
                      variant="outlined"
                      sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                    >
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {DAYS_OF_WEEK[schedule.dayOfWeek]}요일
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                          <Chip
                            label={`입실 ${schedule.expectedCheckIn}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                          <Chip
                            label={`퇴실 ${schedule.expectedCheckOut}`}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                      </Box>
                      <Chip
                        label="저장됨"
                        size="small"
                        color="success"
                      />
                    </Paper>
                  ))}
              </Stack>
            )}
          </Paper>

          {/* 스케줄 편집 테이블 */}
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
            스케줄 편집
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>요일</TableCell>
                  <TableCell>예상 입실</TableCell>
                  <TableCell>예상 퇴실</TableCell>
                  <TableCell align="center">활성화</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {schedules.map((schedule) => (
                  <TableRow
                    key={schedule.dayOfWeek}
                    sx={{ opacity: schedule.isActive ? 1 : 0.5 }}
                  >
                    <TableCell>
                      <Typography fontWeight={600}>
                        {DAYS_OF_WEEK[schedule.dayOfWeek]}요일
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="time"
                        size="small"
                        value={schedule.expectedCheckIn}
                        onChange={(e) =>
                          handleScheduleChange(schedule.dayOfWeek, 'expectedCheckIn', e.target.value)
                        }
                        disabled={!schedule.isActive}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="time"
                        size="small"
                        value={schedule.expectedCheckOut}
                        onChange={(e) =>
                          handleScheduleChange(schedule.dayOfWeek, 'expectedCheckOut', e.target.value)
                        }
                        disabled={!schedule.isActive}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={schedule.isActive}
                        onChange={(e) =>
                          handleScheduleChange(schedule.dayOfWeek, 'isActive', e.target.checked)
                        }
                        color="primary"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveSchedules}
              disabled={isSaving}
              size="large"
            >
              {isSaving ? '저장 중...' : '스케줄 저장'}
            </Button>
          </Box>
        </>
      )}

      {/* ========== 학원 스케줄 추가/수정 다이얼로그 ========== */}
      <Dialog open={academyDialogOpen} onClose={closeAcademyDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingAcademyId ? '학원 스케줄 수정' : '학원 스케줄 추가'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label="학원명"
              fullWidth
              value={academyForm.academy_name}
              onChange={(e) => handleAcademyFormChange('academy_name', e.target.value)}
              placeholder="예: 영어학원, 수학학원"
            />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                요일 선택
              </Typography>
              <FormGroup row>
                {[1, 2, 3, 4, 5, 6, 7].map((day) => (
                  <FormControlLabel
                    key={day}
                    control={
                      <Checkbox
                        checked={academyForm.days_of_week.includes(day)}
                        onChange={() => handleDayToggle(day)}
                      />
                    }
                    label={DAYS_MAP[day]}
                  />
                ))}
              </FormGroup>
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="학원 시작 시간"
                type="time"
                value={academyForm.start_time}
                onChange={(e) => handleAcademyFormChange('start_time', e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="학원 종료 시간"
                type="time"
                value={academyForm.end_time}
                onChange={(e) => handleAcademyFormChange('end_time', e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Box>

            <TextField
              label="이동 시간 (분)"
              type="number"
              value={academyForm.travel_minutes}
              onChange={(e) => handleAcademyFormChange('travel_minutes', parseInt(e.target.value) || 15)}
              InputProps={{ inputProps: { min: 0, max: 120 } }}
              helperText="독서실에서 학원까지 이동하는 데 걸리는 시간입니다."
              fullWidth
            />

            <TextField
              label="여유 시간 (분)"
              type="number"
              value={academyForm.grace_minutes}
              onChange={(e) => handleAcademyFormChange('grace_minutes', parseInt(e.target.value) || 10)}
              InputProps={{ inputProps: { min: 0, max: 60 } }}
              helperText="이동 시간 + 여유 시간이 지나도 외출/복귀하지 않으면 알림이 발송됩니다."
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAcademyDialog}>취소</Button>
          <Button
            variant="contained"
            onClick={saveAcademySchedule}
            disabled={academySaving}
          >
            {academySaving ? '저장 중...' : '저장'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
